import React from "react";
import { Ticket, CalendarClock, RefreshCw, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

function subLine(sub) {
  if (sub.status === "trialing") return `Free trial · ${sub.cancel_at_period_end ? "ends" : "first payment"} ${fmtDate(sub.expires_at)}`;
  if (sub.status === "past_due") return "Payment failed · update your card to keep access";
  if (sub.cancel_at_period_end) return `Cancels ${fmtDate(sub.expires_at)} · access until then`;
  return `Renews ${fmtDate(sub.expires_at)}`;
}

function statusLabel(data, sub, labels) {
  if (sub) {
    const state = sub.status === "trialing" ? "trial" : sub.status.replace("_", " ");
    return `${labels[sub.plan] || "Subscription"} · ${state}${sub.provider === "paypal" ? " · PayPal" : ""}`;
  }
  if (data.active) return `${labels[data.plan] || "Oracle Pass"} · active`;
  return "No active subscription";
}

function expiryLine(data, sub) {
  if (sub) return subLine(sub);
  if (data.active) return `One-time pass · expires ${fmtDate(data.expires_at)}`;
  return "Subscribe below to enter the Oracle";
}

function daysLine(data, sub, daysLeft) {
  if (!data.active) return "Locked";
  const renewing = sub && !sub.cancel_at_period_end && sub.status !== "past_due";
  return `${daysLeft} day${daysLeft === 1 ? "" : "s"} ${renewing ? "until renewal" : "left"}`;
}

function cancelLabel(busy, confirm, sub) {
  if (busy) return <><RefreshCw className="w-3 h-3 mr-2 animate-spin" /> Cancelling…</>;
  if (confirm) return `Confirm — cancel, keep access until ${fmtDate(sub.expires_at)}`;
  return "Cancel subscription";
}

function PaypalControls({ sub, busy, confirmCancel, onCancel }) {
  const note = sub.status === "past_due"
    ? " — payment failed: update your funding source at paypal.com to keep access."
    : ". Update your payment method in your PayPal account.";
  return (
    <div className="mt-4" data-testid="paypal-subscription-controls">
      <div className="font-body text-[11px] text-parchment/50">Billed through PayPal{note}</div>
      {sub.cancel_at_period_end ? (
        <div data-testid="paypal-cancelled-note" className="mt-2 font-body text-xs text-oracle">Cancelled — access continues until {fmtDate(sub.expires_at)}.</div>
      ) : (
        <Button data-testid="paypal-cancel-btn" onClick={onCancel} disabled={busy} variant="outline" className={`mt-2 w-full label-caps bg-transparent ${confirmCancel ? "border-pb_red/60 text-pb_red hover:bg-pb_red/10 hover:text-pb_red" : "border-oracle/40 text-oracle hover:bg-oracle/10 hover:text-oracle"}`}>
          {cancelLabel(busy, confirmCancel, sub)}
        </Button>
      )}
    </div>
  );
}

export function ProfileStatusCard({ data, sub, labels, portalBusy, confirmCancel, onManage, onCancelPaypal }) {
  const daysLeft = data.expires_at ? Math.max(0, Math.ceil((new Date(data.expires_at) - Date.now()) / 86400000)) : 0;
  return (
    <div data-testid="profile-pass-status" className={`rounded-xl border p-5 ${data.active ? "border-oracle/40 bg-oracle/5" : "border-white/10 bg-black/20"}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps flex items-center gap-2 text-oracle">
            <Ticket className="w-3 h-3" strokeWidth={1.5} />
            {statusLabel(data, sub, labels)}
          </div>
          <div className="font-heading text-4xl mt-2 tabular-nums" data-testid="profile-days-left">{daysLine(data, sub, daysLeft)}</div>
          <div className="font-body text-xs text-parchment/60 mt-1 flex items-center gap-1.5" data-testid="profile-expires">
            <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.5} />
            {expiryLine(data, sub)}
          </div>
        </div>
        <div className="text-right">
          <div className="label-caps text-parchment/50">Total spent</div>
          <div className="font-heading text-2xl text-parchment" data-testid="profile-total-spent">${data.total_spent.toFixed(2)}</div>
        </div>
      </div>
      {sub && sub.provider !== "paypal" && (
        <Button data-testid="manage-subscription-btn" onClick={onManage} disabled={portalBusy} variant="outline" className="mt-4 w-full h-auto py-2.5 whitespace-normal border-oracle/40 text-oracle hover:bg-oracle/10 hover:text-oracle label-caps bg-transparent">
          {portalBusy ? <><RefreshCw className="w-3 h-3 mr-2 animate-spin" /> Opening Stripe…</> : <><ExternalLink className="w-3 h-3 mr-2 shrink-0" strokeWidth={1.5} /> <span>Manage subscription<span className="hidden sm:inline"> · cancel, switch plan, update card, invoices</span></span></>}
        </Button>
      )}
      {sub && sub.provider === "paypal" && <PaypalControls sub={sub} busy={portalBusy} confirmCancel={confirmCancel} onCancel={onCancelPaypal} />}
    </div>
  );
}
